/**
 * SettingsSalesCard — Iter 153.17 (Feb 8, 2026, Yash)
 *
 * Admin-managed sale promotions. Lists all sales with status badges
 * (active / upcoming / ended) and provides create / edit / delete.
 *
 * Backend: /api/admin/sales (GET / POST / PUT / DELETE).
 *
 * Behaviour the AI obeys when a sale is active (handled in
 * server.py `_format_sale_block_for_prompt`):
 *   • Offer EXACTLY discount_percent off the website price
 *   • Replaces the 10% first-order courtesy (no stacking)
 *   • Hard ceiling: discount + 5% wiggle for negotiation
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Tag, Plus, Trash2, Edit3, Loader2, Calendar, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const map = {
    active:   { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300", label: "ACTIVE" },
    upcoming: { bg: "bg-blue-100",    text: "text-blue-800",    border: "border-blue-300",    label: "UPCOMING" },
    ended:    { bg: "bg-slate-100",   text: "text-slate-600",   border: "border-slate-300",   label: "ENDED" },
  };
  const s = map[status] || map.ended;
  return (
    <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border} tracking-wider`}>
      {s.label}
    </span>
  );
};

const SaleForm = ({ initial, onSubmit, onCancel, saving }) => {
  const [name, setName] = useState(initial?.name || "");
  const [pct, setPct] = useState(initial?.discount_percent ?? 20);
  const [start, setStart] = useState(initial?.start_date || new Date().toISOString().split("T")[0]);
  const [end, setEnd] = useState(initial?.end_date || "");
  const [india, setIndia] = useState(initial?.applies_to_india ?? true);
  const [overseas, setOverseas] = useState(initial?.applies_to_overseas ?? false);

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Sale name required"); return; }
    if (!end) { toast.error("End date required"); return; }
    if (end < start) { toast.error("End date must be on or after start"); return; }
    if (!india && !overseas) { toast.error("Select at least one audience"); return; }
    onSubmit({
      name: name.trim(),
      discount_percent: Number(pct),
      start_date: start,
      end_date: end,
      applies_to_india: india,
      applies_to_overseas: overseas,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3 bg-violet-50/50 border border-violet-200 rounded-xl p-3" data-testid="sale-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Sale name</span>
          <input
            type="text" required maxLength={100}
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Diwali 20% Off"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
            data-testid="sale-input-name"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Discount %</span>
          <input
            type="number" required min={1} max={90} step={1}
            value={pct} onChange={(e) => setPct(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
            data-testid="sale-input-pct"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Start date</span>
          <input
            type="date" required
            value={start} onChange={(e) => setStart(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
            data-testid="sale-input-start"
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">End date</span>
          <input
            type="date" required
            value={end} onChange={(e) => setEnd(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
            data-testid="sale-input-end"
          />
        </label>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Audience:</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={india} onChange={(e) => setIndia(e.target.checked)} data-testid="sale-input-india" />
          <span className="text-sm">🇮🇳 India</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={overseas} onChange={(e) => setOverseas(e.target.checked)} data-testid="sale-input-overseas" />
          <span className="text-sm">🌏 Overseas</span>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit" disabled={saving}
          className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-bold rounded-lg flex items-center gap-2"
          data-testid="sale-submit-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {initial ? "Save changes" : "Create sale"}
        </button>
        <button
          type="button" onClick={onCancel}
          className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-bold rounded-lg"
          data-testid="sale-cancel-btn"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const SettingsSalesCard = ({ user }) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const fetchSales = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/api/admin/sales`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setSales(data?.sales || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sales");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) fetchSales(); }, [isAdmin]);

  const create = async (payload) => {
    setSaving(true);
    try {
      const t = localStorage.getItem("token");
      await axios.post(`${API}/api/admin/sales`, payload, {
        headers: { Authorization: `Bearer ${t}` },
      });
      toast.success("Sale created — AI will start offering it immediately");
      setShowForm(false);
      await fetchSales();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const update = async (id, payload) => {
    setSaving(true);
    try {
      const t = localStorage.getItem("token");
      await axios.put(`${API}/api/admin/sales/${id}`, payload, {
        headers: { Authorization: `Bearer ${t}` },
      });
      toast.success("Sale updated");
      setEditing(null);
      await fetchSales();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (sale) => {
    if (!window.confirm(`Delete sale "${sale.name}"? AI will stop offering it immediately.`)) return;
    try {
      const t = localStorage.getItem("token");
      await axios.delete(`${API}/api/admin/sales/${sale.id}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      toast.success("Sale deleted");
      await fetchSales();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!isAdmin) return null;

  const activeCount = sales.filter((s) => s.status === "active").length;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="settings-sales-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-rose-50 border border-rose-200 rounded-xl flex items-center justify-center">
            <Tag size={18} className="text-rose-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Sales & Promotions
            </h2>
            <p className="text-xs text-slate-500">
              {activeCount > 0
                ? `${activeCount} active sale${activeCount === 1 ? "" : "s"} — AI is offering automatically`
                : "Configure site-wide sales the AI will offer to customers"}
            </p>
          </div>
        </div>
        {!showForm && !editing && (
          <button
            onClick={() => setShowForm(true)}
            className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5"
            data-testid="sale-new-btn"
          >
            <Plus size={13} /> New sale
          </button>
        )}
      </div>

      {showForm && (
        <SaleForm
          onSubmit={create}
          onCancel={() => setShowForm(false)}
          saving={saving}
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-4 justify-center text-xs">
          <Loader2 size={14} className="animate-spin" /> Loading sales…
        </div>
      ) : sales.length === 0 ? (
        <div className="text-center py-6 text-slate-400 text-sm" data-testid="sales-empty">
          No sales configured. Create one to start offering automatic discounts.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="sales-list">
          {sales.map((s) => (
            <li
              key={s.id}
              className={`border rounded-xl p-3 ${s.status === "active" ? "border-emerald-300 bg-emerald-50/30" : s.status === "upcoming" ? "border-blue-200 bg-blue-50/20" : "border-slate-200 bg-slate-50/40"}`}
              data-testid={`sale-row-${s.id}`}
            >
              {editing?.id === s.id ? (
                <SaleForm
                  initial={s}
                  onSubmit={(payload) => update(s.id, payload)}
                  onCancel={() => setEditing(null)}
                  saving={saving}
                />
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-slate-900 truncate">{s.name}</h3>
                      <StatusBadge status={s.status} />
                      <span className="text-xs font-extrabold text-rose-700">{s.discount_percent}% off</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar size={10} /> {s.start_date} → {s.end_date}
                      </span>
                      <span>
                        Audience: {s.applies_to_india && "🇮🇳 India"}
                        {s.applies_to_india && s.applies_to_overseas && " + "}
                        {s.applies_to_overseas && "🌏 Overseas"}
                      </span>
                      {s.created_by && <span className="text-slate-400">· by {s.created_by}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setEditing(s)}
                      className="p-1.5 text-slate-500 hover:text-violet-600 hover:bg-violet-50 rounded"
                      title="Edit sale"
                      data-testid={`sale-edit-${s.id}`}
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => remove(s)}
                      className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded"
                      title="Delete sale"
                      data-testid={`sale-delete-${s.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Behaviour explainer — keeps admins clear on the AI's stacking rules. */}
      <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5 space-y-0.5">
        <p><strong>How the AI applies an active sale:</strong></p>
        <p>• Offers exactly the sale % off the website price (replaces the 10% first-order courtesy — no stacking)</p>
        <p>• If customer pushes back, AI may stretch up to <em>sale + 5%</em> as a hard ceiling. Beyond that → transfer to Sales.</p>
        <p>• Hard cut-off at end_date 23:59:59 IST — sale stops being offered automatically. No manual toggle needed.</p>
      </div>
    </div>
  );
};

export default SettingsSalesCard;
